import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, XCircle, ChevronDown, ChevronRight, X } from 'lucide-react';
import { api } from '../services/api';
import Avatar from '../components/Avatar';
import AgencyLogo from '../components/AgencyLogo';
import { useIsMobile } from '../hooks/useIsMobile';
import type { PendingChange } from '../types';

const PendingChangesPage: React.FC = () => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const qc = useQueryClient();

  const [expanded, setExpanded] = useState<string | null>(null);
  const [rejectTarget, setRejectTarget] = useState<string | null>(null);
  const [rejectNote, setRejectNote] = useState('');
  const [approvingId, setApprovingId] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['pending-changes'],
    queryFn: () => api.getPendingChanges({ limit: 200, status_id: 1 }),
  });

  const changes: PendingChange[] = data?.data ?? [];
  const total: number = data?.meta?.total ?? changes.length;

  const removeFromCache = (id: string) => {
    qc.setQueryData(['pending-changes'], (old: any) => {
      if (!old) return old;
      return { ...old, data: (old.data ?? []).filter((c: PendingChange) => c.id !== id) };
    });
  };

  const approveMutation = useMutation({
    mutationFn: (id: string) => api.approvePendingChange(id),
    onMutate: async (id: string) => {
      setApprovingId(id);
      await qc.cancelQueries({ queryKey: ['pending-changes'] });
      const previous = qc.getQueryData(['pending-changes']);
      removeFromCache(id);
      return { previous };
    },
    onError: (err: unknown, _id: string, context: any) => {
      qc.setQueryData(['pending-changes'], context?.previous);
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      alert(`Approve failed: ${msg ?? 'Unknown error'}`);
    },
    onSettled: () => {
      setApprovingId(null);
      qc.invalidateQueries({ queryKey: ['pending-changes'] });
      qc.invalidateQueries({ queryKey: ['contacts'] });
      qc.invalidateQueries({ queryKey: ['contact'] });
      qc.invalidateQueries({ queryKey: ['kpi'] });
      window.dispatchEvent(new Event('iad2:counts-stale'));
    },
  });

  const rejectMutation = useMutation({
    mutationFn: () => api.rejectPendingChange(rejectTarget!, rejectNote),
    onMutate: async () => {
      if (!rejectTarget) return;
      await qc.cancelQueries({ queryKey: ['pending-changes'] });
      const previous = qc.getQueryData(['pending-changes']);
      removeFromCache(rejectTarget);
      return { previous };
    },
    onError: (_err: unknown, _v: void, context: any) => {
      qc.setQueryData(['pending-changes'], context?.previous);
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['pending-changes'] });
      window.dispatchEvent(new Event('iad2:counts-stale'));
      setRejectTarget(null);
      setRejectNote('');
    },
  });

  const formatValue = (v: unknown): string => {
    if (v == null) return '—';
    if (typeof v === 'object') return JSON.stringify(v);
    return String(v);
  };

  const SOURCE_LABELS: Record<string, string> = {
    'Update Portal': 'Update Portal',
    'URL_SUBMISSION': 'Update Portal',
    'HR_SYNC': 'HR Sync',
    'MANUAL': 'Manual',
    'import': 'Import',
  };
  const sourceLabel = (raw: string | undefined) => raw ? (SOURCE_LABELS[raw] ?? raw) : null;

  const STATUS_COLORS: Record<string, { bg: string; color: string }> = {
    Pending: { bg: 'var(--amber-50)', color: 'var(--amber-text)' },
    Approved: { bg: 'var(--green-50)', color: 'var(--green-700)' },
    Rejected: { bg: 'var(--red-50)', color: 'var(--red-700)' },
    Skipped: { bg: 'var(--gray-100)', color: 'var(--gray-500)' },
  };

  return (
    <div style={{ paddingBottom: 32 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <h2 style={{ margin: 0, fontSize: 18 }}>
          Pending Changes{total > 0 && <span style={{ fontSize: 14, color: 'var(--gray-500)', fontWeight: 400, marginLeft: 8 }}>({total})</span>}
        </h2>
      </div>

      {isLoading ? (
        <div style={{ textAlign: 'center', padding: 32, color: 'var(--gray-500)' }}>Loading…</div>
      ) : changes.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', color: 'var(--gray-500)', padding: 40 }}>
          No pending changes
        </div>
      ) : (
        changes.map((c) => {
          const isDeleteRequest =
            c.proposed_data?._action === 'delete' ||
            c.proposed_data?.action === 'delete';

          // Normalise proposed_data into a unified { field: { old, new } } map
          // regardless of whether it comes from HR sync ({ diff: { ... } })
          // or the update portal ({ field: { old, new }, ... }).
          const buildDiff = (data: typeof c.proposed_data) => {
            const skip = new Set(['hr_record', 'match_type', 'employee_id', 'diff', '_action', 'action', 'reason']);
            if (data.diff && typeof data.diff === 'object') {
              return data.diff as Record<string, { old: unknown; new: unknown }>;
            }
            const out: Record<string, { old: unknown; new: unknown }> = {};
            for (const [k, v] of Object.entries(data)) {
              if (skip.has(k)) continue;
              if (v && typeof v === 'object' && 'new' in (v as object)) {
                out[k] = v as { old: unknown; new: unknown };
              }
            }
            return out;
          };
          const diff = buildDiff(c.proposed_data);
          const diffKeys = Object.keys(diff);
          // Live contact values returned by the JOIN — prefer over stale stored old values
          const liveValues: Record<string, unknown> = {
            title:         c.current_title,
            email_primary: c.current_email,
            office_group:  c.current_office_group,
          };
          const isOpen = expanded === c.id;
          const colors = STATUS_COLORS[c.status_name ?? ''] ?? { bg: 'var(--gray-100)', color: 'var(--gray-500)' };

          return (
            <div
              key={c.id}
              className="card"
              style={{
                marginBottom: 10,
                padding: 0,
                borderLeft: isDeleteRequest ? '3px solid var(--red)' : undefined,
              }}
            >
              {/* Row header */}
              <div
                style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'stretch' : 'center', padding: '12px 16px', cursor: 'pointer', gap: isMobile ? 8 : 10 }}
                onClick={() => setExpanded(isOpen ? null : c.id)}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ color: 'var(--gray-500)' }}>
                    {isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                  </span>
                  <Avatar name={c.contact_name ?? 'Unknown Contact'} size={26} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <span
                      style={{ fontWeight: 500, cursor: 'pointer', color: 'var(--blue)' }}
                      onClick={(e) => { e.stopPropagation(); if (c.contact_id) navigate(`/contacts/${c.contact_id}`); }}
                    >
                      {c.contact_name ?? c.contact_id ?? 'Unknown Contact'}
                    </span>
                    {c.agency_name && (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, marginLeft: 8 }}>
                        <AgencyLogo name={c.agency_name} size={20} />
                        {!isMobile && <span style={{ fontSize: 14, color: 'var(--gray-500)' }}>{c.agency_name}</span>}
                      </span>
                    )}
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  {isDeleteRequest && (
                    <span className="chip" style={{ fontSize: 12, background: 'var(--red-50)', color: 'var(--red)', border: '1px solid #fca5a5' }}>
                      🗑 Deletion Request
                    </span>
                  )}
                  {!isDeleteRequest && c.proposed_data?.match_type && (
                    <span className="chip" style={{ fontSize: 12 }}>{c.proposed_data.match_type}</span>
                  )}
                  {sourceLabel(c.source_name) && (
                    <span className="chip" style={{ fontSize: 12 }}>{sourceLabel(c.source_name)}</span>
                  )}
                  <span
                    className="pill"
                    style={{ background: colors.bg, color: colors.color }}
                  >
                    {c.status_name ?? 'Unknown'}
                  </span>
                  <span style={{ fontSize: 12, color: 'var(--gray-500)' }}>
                    {c.submitted_at
                      ? new Date(c.submitted_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                      : '—'}
                  </span>
                  {(c.status_name === 'Pending') && (
                    <div style={{ display: 'flex', gap: 6, width: isMobile ? '100%' : undefined }} onClick={(e) => e.stopPropagation()}>
                      <button
                        className="btn btn-secondary"
                        style={{ padding: '4px 10px', fontSize: 12, color: isDeleteRequest ? 'var(--red)' : 'var(--green)', flex: isMobile ? 1 : undefined }}
                        disabled={approvingId === c.id}
                        onClick={() => approveMutation.mutate(c.id)}
                      >
                        <CheckCircle size={12} />
                        {approvingId === c.id ? 'Approving…' : isDeleteRequest ? 'Archive Contact' : 'Approve'}
                      </button>
                      <button
                        className="btn btn-secondary"
                        style={{ padding: '4px 10px', fontSize: 12, color: 'var(--red)', flex: isMobile ? 1 : undefined }}
                        onClick={() => { setRejectTarget(c.id); setRejectNote(''); }}
                      >
                        <XCircle size={12} />
                        Reject
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Expanded diff */}
              {isOpen && (
                <div style={{ borderTop: '1px solid var(--gray-100)', padding: '12px 16px' }}>
                  {isDeleteRequest ? (
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      padding: '8px 12px',
                      background: 'var(--red-50)',
                      border: '1px solid var(--red)',
                      borderRadius: 6,
                      color: 'var(--red)',
                      fontSize: 14,
                      fontWeight: 500,
                    }}>
                      ⚠ Contact marked for removal by agency liaison
                      {c.proposed_data?.reason && (
                        <span style={{ fontWeight: 400, color: 'var(--gray-500)', fontSize: 12 }}>
                          — {String(c.proposed_data.reason)}
                        </span>
                      )}
                    </div>
                  ) : diffKeys.length === 0 ? (
                    <p style={{ fontSize: 14, color: 'var(--gray-500)', margin: 0 }}>
                      No field-level diff available.
                      {c.proposed_data?.employee_id && ` Employee ID: ${c.proposed_data.employee_id}`}
                      {c.source_name && ` (Source: ${sourceLabel(c.source_name)})`}
                    </p>
                  ) : (
                    <table className="tbl" style={{ fontSize: 14 }}>
                      <thead>
                        <tr>
                          <th>Field</th>
                          <th>Current</th>
                          <th>Proposed</th>
                        </tr>
                      </thead>
                      <tbody>
                        {diffKeys.map((field) => {
                          const entry = diff[field] as { old: unknown; new: unknown };
                          const currentVal = liveValues[field] !== undefined
                            ? liveValues[field]
                            : entry.old;
                          return (
                            <tr key={field}>
                              <td style={{ fontWeight: 500 }}>{field}</td>
                              <td className="diff-old">{formatValue(currentVal)}</td>
                              <td className="diff-new">{formatValue(entry.new)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                  {c.resolution_note && (
                    <p style={{ fontSize: 12, color: 'var(--gray-500)', marginTop: 8, marginBottom: 0 }}>
                      Note: {c.resolution_note}
                    </p>
                  )}
                </div>
              )}
            </div>
          );
        })
      )}

      {/* Reject Modal */}
      {rejectTarget && (
        <div className="modal-overlay" onClick={() => setRejectTarget(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 style={{ margin: 0 }}>Reject Change</h3>
              <button className="btn btn-ghost" onClick={() => setRejectTarget(null)}><X size={16} /></button>
            </div>
            <label className="field-label">Rejection Reason</label>
            <textarea
              className="field-input"
              rows={3}
              value={rejectNote}
              onChange={(e) => setRejectNote(e.target.value)}
              placeholder="Explain why this change is rejected…"
              maxLength={1000}
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
              <button className="btn btn-secondary" onClick={() => setRejectTarget(null)}>Cancel</button>
              <button
                className="btn btn-primary"
                style={{ background: 'var(--red)' }}
                disabled={!rejectNote.trim() || rejectMutation.isPending}
                onClick={() => rejectMutation.mutate()}
              >
                {rejectMutation.isPending ? 'Rejecting…' : 'Reject'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PendingChangesPage;
